// 送出之後畫面長什麼樣，是一張表。純邏輯、不 import lit —— 這裡決定「要不要自動彈」「popup
// 放結果還是致謝」「結束鈕是關 popup 還是讓 widget 消失」「收合槽放什麼」，把決定翻成
// TemplateResult 是 survey-element 的事。
//
// 攤成表之前這四個決定分散在七個 private method 與三個布林裡，要回答「manual 收到 410 會怎樣」
// 得在七處之間彈跳，而每一處都只看得到自己那一格。

/** 版面。overlay 三種共用收合插槽 + dialog 外殼；inline 直接 in-flow 渲染。 */
export type Layout = 'inline' | 'sidebar' | 'modal' | 'manual'

/**
 * 送出之後的結局。
 *
 * 「已結束又手上有統計」不在其中：統計只從送出成功那次的 201 來，而一位讀者一票 ——
 * `survey-element` 的 `load()` 在送出定案後就不再拿問卷，元素回不到作答狀態，也就沒有第二次
 * 送出可以撞上 410。要確認那條路真的關著，看 `survey-element.test.ts` 的
 * `describe('<pulse-survey> counts one vote per reader')`。
 */
export type PostSubmitOutcome
  = | 'ended' // 問卷已結束（410），手上沒有統計。
    | 'result' // 送出成功，且拿到可顯示的結果。
    | 'plain' // 送出成功，但沒有可顯示的結果（問卷不呈現結果，或結果一題都不剩）。

/** 送出後這一格的呈現決定。以外殼分岔：in-flow 那格沒有 popup 也沒有收合槽可談。 */
export type PostSubmitView
  = | {
    /** 不走 overlay 殼，原地 in-flow 渲染結束畫面。 */
    shell: 'in-flow'
  }
  | {
    /**
     * 走 overlay 殼。送出當下 popup 一律自動打開 —— 不另設一個欄位，因為十一格全都要開，
     * 一個恆為 true 的欄位只會讓讀表的人以為它有時是 false。真的出現「有殼但不自動開」的
     * 格子時再加回來。
     */
    shell: 'overlay'
    /** popup 裡放什麼。 */
    popup: 'results' | 'thank-you'
    /** popup 的「結束」鈕做什麼：關掉 popup，或讓整個 widget 從頁面消失。 */
    onFinish: 'close-popup' | 'dismiss-widget'
    /** popup 關閉後版位上留下什麼。 */
    collapsed: 'summary' | 'thank-you' | 'none'
  }

// 兩層都是窮盡的 Record：加一個版面或一種結局，這張表會編不過。
//
// 兩格與直覺不同，值本身就是規則，不必再推導：
//   inline + ended  —— inline 平常完全不走 overlay，但收到 410 時會彈一個 modal 告知讀者。
//   modal + ended   —— modal 送出成功後不留殘留，ended 卻留一張摘要卡。
// 兩者若要改是產品決定，改的是這裡的一格。
const VIEWS: Record<Layout, Record<PostSubmitOutcome, PostSubmitView>> = {
  inline: {
    ended: { shell: 'overlay', popup: 'thank-you', onFinish: 'close-popup', collapsed: 'summary' },
    result: { shell: 'overlay', popup: 'results', onFinish: 'close-popup', collapsed: 'thank-you' },
    // 唯一一格 in-flow：送出成功又沒有結果可看，沒有理由為了一句「感謝您完成填寫」蓋掉整頁。
    plain: { shell: 'in-flow' },
  },
  sidebar: {
    ended: { shell: 'overlay', popup: 'thank-you', onFinish: 'close-popup', collapsed: 'summary' },
    result: { shell: 'overlay', popup: 'results', onFinish: 'close-popup', collapsed: 'thank-you' },
    // 唯一一格 dismiss-widget：側欄沒有結果可回頭看，按下結束就該讓出版位。
    plain: { shell: 'overlay', popup: 'thank-you', onFinish: 'dismiss-widget', collapsed: 'thank-you' },
  },
  modal: {
    ended: { shell: 'overlay', popup: 'thank-you', onFinish: 'close-popup', collapsed: 'summary' },
    result: { shell: 'overlay', popup: 'results', onFinish: 'close-popup', collapsed: 'none' },
    plain: { shell: 'overlay', popup: 'thank-you', onFinish: 'close-popup', collapsed: 'none' },
  },
  // manual 是 headless：沒有觸發就不佔版面，所以任何結局都不留收合內容。
  manual: {
    ended: { shell: 'overlay', popup: 'thank-you', onFinish: 'close-popup', collapsed: 'none' },
    result: { shell: 'overlay', popup: 'results', onFinish: 'close-popup', collapsed: 'none' },
    plain: { shell: 'overlay', popup: 'thank-you', onFinish: 'close-popup', collapsed: 'none' },
  },
}

// 把「已結束嗎」「手上有沒有可顯示的統計」翻成一種結局。已結束時不必再問有沒有統計：
// 讀者手上那份統計只可能來自已經成功的那次送出，而那次之後就沒有下一次送出了。
export function postSubmitOutcome(ended: boolean, hasResult: boolean): PostSubmitOutcome {
  if (ended)
    return 'ended'
  return hasResult ? 'result' : 'plain'
}

/** 這個版面、這種結局，畫面長怎樣。 */
export function postSubmitView(layout: Layout, outcome: PostSubmitOutcome): PostSubmitView {
  return VIEWS[layout][outcome]
}

// dialog 外殼的形狀：sidebar → 側欄收合卡；其餘 → bottom-sheet（手機）/ modal（桌機）。
// 對 inline 也有答案，因為 inline 在 ended 那格同樣會走 overlay 殼。
export function overlayVariant(layout: Layout): 'sidebar' | 'modal' {
  return layout === 'sidebar' ? 'sidebar' : 'modal'
}
